<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import {
  NSpace,
  NSwitch,
  NRadioGroup,
  NRadioButton,
  NText,
  NButton,
  NSelect,
  NInput,
} from 'naive-ui';
import { formatTimeInTimezone, getTimezoneOffset } from '@awesomeclocks/utils';
import type { WorldClockWidgetConfig } from '@awesomeclocks/types';
import WidgetWrapper from './WidgetWrapper.vue';

interface Props {
  widget: WorldClockWidgetConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  remove: [];
  updateName: [name: string];
  updateSettings: [settings: WorldClockWidgetConfig['settings']];
}>();

const currentTime = ref(new Date());
let intervalId: number | null = null;

const updateTime = () => {
  currentTime.value = new Date();
};

onMounted(() => {
  intervalId = window.setInterval(updateTime, 1000);
});

onUnmounted(() => {
  if (intervalId !== null) {
    clearInterval(intervalId);
  }
});

// Common timezones for the selector
const timezoneOptions = [
  { label: 'UTC', value: 'UTC' },
  { label: 'New York', value: 'America/New_York' },
  { label: 'Los Angeles', value: 'America/Los_Angeles' },
  { label: 'Chicago', value: 'America/Chicago' },
  { label: 'London', value: 'Europe/London' },
  { label: 'Paris', value: 'Europe/Paris' },
  { label: 'Tokyo', value: 'Asia/Tokyo' },
  { label: 'Hong Kong', value: 'Asia/Hong_Kong' },
  { label: 'Dubai', value: 'Asia/Dubai' },
  { label: 'Sydney', value: 'Australia/Sydney' },
];

const newTimezone = ref('');
const newLabel = ref('');
const showAddForm = ref(false);

const updateFormat = (format: '12h' | '24h') => {
  emit('updateSettings', { ...props.widget.settings, format });
};

const updateShowSeconds = (showSeconds: boolean) => {
  emit('updateSettings', { ...props.widget.settings, showSeconds });
};

const addTimezone = () => {
  if (newTimezone.value && newLabel.value) {
    const timezones = [
      ...props.widget.settings.timezones,
      { timezone: newTimezone.value, label: newLabel.value },
    ];
    emit('updateSettings', { ...props.widget.settings, timezones });
    newTimezone.value = '';
    newLabel.value = '';
    showAddForm.value = false;
  }
};

const removeTimezone = (index: number) => {
  const timezones = props.widget.settings.timezones.filter((_, i) => i !== index);
  emit('updateSettings', { ...props.widget.settings, timezones });
};

const getTimeForTimezone = (timezone: string) => {
  return formatTimeInTimezone(
    timezone,
    props.widget.settings.format,
    props.widget.settings.showSeconds,
    currentTime.value
  );
};
</script>

<template>
  <WidgetWrapper
    :widget="widget"
    @remove="emit('remove')"
    @update-name="emit('updateName', $event)"
  >
    <NSpace vertical :size="16" style="width: 100%">
      <!-- World Clocks Display -->
      <div class="world-clocks">
        <div
          v-for="(tz, index) in widget.settings.timezones"
          :key="`${tz.timezone}-${index}`"
          class="timezone-item"
        >
          <NSpace justify="space-between" align="center">
            <div class="timezone-info">
              <NText class="timezone-label" strong>{{ tz.label }}</NText>
              <NText class="timezone-offset" :depth="3" style="font-size: 0.8rem">
                {{ getTimezoneOffset(tz.timezone) }}
              </NText>
            </div>
            <div class="timezone-actions">
              <NText class="timezone-time" :depth="1">
                {{ getTimeForTimezone(tz.timezone) }}
              </NText>
              <NButton size="tiny" quaternary @click="removeTimezone(index)"> ✕ </NButton>
            </div>
          </NSpace>
        </div>

        <div v-if="widget.settings.timezones.length === 0" class="empty-state">
          <NText :depth="3">No timezones added. Click below to add one.</NText>
        </div>
      </div>

      <!-- Add Timezone Form -->
      <div v-if="showAddForm" class="add-form">
        <NSpace vertical :size="8">
          <NSelect
            v-model:value="newTimezone"
            :options="timezoneOptions"
            placeholder="Select timezone"
            size="small"
          />
          <NInput
            v-model:value="newLabel"
            placeholder="Label (e.g., Office)"
            size="small"
          />
          <NSpace>
            <NButton size="small" type="primary" @click="addTimezone">Add</NButton>
            <NButton size="small" @click="showAddForm = false">Cancel</NButton>
          </NSpace>
        </NSpace>
      </div>

      <NButton v-else size="small" block @click="showAddForm = true">
        + Add Timezone
      </NButton>

      <!-- Settings -->
      <NSpace vertical :size="8" class="settings">
        <NSpace align="center" justify="space-between">
          <NText>Format:</NText>
          <NRadioGroup
            :value="widget.settings.format"
            size="small"
            @update:value="updateFormat"
          >
            <NRadioButton value="12h">12h</NRadioButton>
            <NRadioButton value="24h">24h</NRadioButton>
          </NRadioGroup>
        </NSpace>

        <NSpace align="center" justify="space-between">
          <NText>Show Seconds:</NText>
          <NSwitch
            :value="widget.settings.showSeconds"
            @update:value="updateShowSeconds"
          />
        </NSpace>
      </NSpace>
    </NSpace>
  </WidgetWrapper>
</template>

<style scoped>
.world-clocks {
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
}

.timezone-item {
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.timezone-item:last-child {
  border-bottom: none;
}

.timezone-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.timezone-label {
  font-size: 1rem;
}

.timezone-offset {
  font-size: 0.8rem;
}

.timezone-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.timezone-time {
  font-size: 1.2rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.05em;
  min-width: 120px;
  text-align: right;
}

.empty-state {
  padding: 32px;
  text-align: center;
}

.add-form {
  padding: 12px;
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.settings {
  width: 100%;
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
